import CheckIcon from "../../../../svg/check-icon";
import ChevronDownSm from "../../../../svg/chevron-down-sm";
import ChevronUp from "../../../../svg/chevron-up";
import ClearIcon from "../../../../svg/clear-icon";
import LoadingSpinner from "../../../../svg/loading-spinner";
import SearchIconSm from "../../../../svg/search-icon-sm";
import {
  useAsyncSearchableSelect,
  type AsyncSearchableSelectOption,
} from "../hooks/useAsyncSearchableSelect";

interface AsyncSearchableSelectProps {
  id: string;
  label: string;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
  hint?: string;
  error?: string;
  value?: string;
  defaultValue?: string;
  initialOptions?: AsyncSearchableSelectOption[];
  onSearch: (query: string) => Promise<AsyncSearchableSelectOption[]>;
  onOptionChange: (data: { name: string; value: string }) => void;
  searchPlaceholder?: string;
  noResultsMessage?: string;
  minSearchLength?: number;
  debounceMs?: number;
  /** When true, Enter commits the typed search text as the value. */
  isTypeable?: boolean;
  /**
   * When true, a finished search with no matches lists the trimmed query as a selectable
   * option and Enter commits that text.
   */
  commitSearchOnEnterWhenNoResults?: boolean;
}

const AsyncSearchableSelect = ({
  id,
  label,
  placeholder,
  required = false,
  disabled = false,
  hint,
  error,
  value,
  defaultValue,
  initialOptions = [],
  onSearch,
  onOptionChange,
  searchPlaceholder = "Search...",
  noResultsMessage = "No results found",
  minSearchLength = 2,
  debounceMs = 300,
  isTypeable = false,
  commitSearchOnEnterWhenNoResults = false,
}: AsyncSearchableSelectProps) => {
  const {
    selected,
    isOpen,
    searchQuery,
    isLoading,
    errorText,
    dropdownPosition,
    displayedOptions,
    triggerRef,
    dropdownRef,
    searchInputRef,
    handleToggle,
    handleSelect,
    handleSearchChange,
    handleKeyDown,
    handleClearSearch,
  } = useAsyncSearchableSelect({
    id,
    initialOptions,
    onSearch,
    onOptionChange,
    value,
    defaultValue,
    disabled,
    error,
    minSearchLength,
    debounceMs,
    isTypeable,
    commitSearchOnEnterWhenNoResults,
  });

  return (
    <div className="searchable-select-container">
      <div
        className={`searchable-select ${errorText ? "error" : ""} ${disabled ? "disabled" : ""}`}
      >
        <div className="label">
          {label} {required && <span className="required"> *</span>}
        </div>
        <div
          className={isOpen ? "selected-text open" : "selected-text"}
          onClick={handleToggle}
          ref={triggerRef}
        >
          {selected.label === "" ? (
            <div className="placeholder">{placeholder}</div>
          ) : (
            <div className="selected-value">{selected.label}</div>
          )}
          {isOpen ? <ChevronUp /> : <ChevronDownSm />}
        </div>
        {hint && <div className="select-hint">{hint}</div>}
        {errorText && <div className="error-message">{errorText}</div>}
        {isOpen && (
          <div
            className={
              dropdownPosition === "bottom"
                ? "searchable-options bottom"
                : "searchable-options top"
            }
            ref={dropdownRef}
          >
            <div className="search-box">
              <SearchIconSm />
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                aria-label="Search options"
              />
              {searchQuery.length > 0 && (
                <button
                  type="button"
                  className="search-clear"
                  aria-label="Clear search"
                  onClick={handleClearSearch}
                >
                  <ClearIcon />
                </button>
              )}
            </div>
            <div className="options-list">
              {isLoading ? (
                <LoadingSpinner />
              ) : displayedOptions.length === 0 ? (
                <div className="no-results">{noResultsMessage}</div>
              ) : (
                displayedOptions.map((option) => (
                  <div
                    key={option.value}
                    className={
                      selected.value === option.value
                        ? "option selected"
                        : "option"
                    }
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                    {selected.value === option.value && <CheckIcon />}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AsyncSearchableSelect;
